import React, { useState, useEffect } from 'react';
import { X, Check, Search } from 'lucide-react';
import { registrationService } from '../../../services/registrationService';
import { adminService } from '../../../services/adminService';
import { exportService } from '../../../services/exportService';

const PlayerSelectionModal = ({ isOpen, onClose, onConfirm, filters = {} }) => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadPlayers();
    } else {
      setSelectedPlayers([]);
      setSearchTerm('');
    }
  }, [isOpen, filters]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = players.filter(player => {
        const fullName = `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.toLowerCase();
        const schoolName = (player.schoolName || '').toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || schoolName.includes(searchTerm.toLowerCase());
      });
      setFilteredPlayers(filtered);
    } else {
      setFilteredPlayers(players);
    }
  }, [searchTerm, players]);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      // Fetch registrations with filters
      const result = await adminService.getFilteredRegistrations(filters, { page: 1, pageSize: 10000 });
      const registrations = result?.data || [];

      // Fetch players for each registration
      const allPlayers = [];
      for (const reg of registrations) {
        try {
          const regPlayers = await registrationService.getPlayersByRegistration(reg.dbId || reg.id);
          (regPlayers || []).forEach(player => {
            allPlayers.push({
              ...player,
              playerId: player.id,
              schoolName: reg.schoolName || 'N/A',
              ageCategory: reg.ageCategory || reg.age_category,
              registrationId: reg.dbId || reg.id
            });
          });
        } catch (err) {
          console.error(`Error fetching players for registration ${reg.id}:`, err);
        }
      }

      setPlayers(allPlayers);
      setFilteredPlayers(allPlayers);
    } catch (error) {
      console.error('Error loading players:', error);
      alert('Failed to load players');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlayer = (playerId) => {
    setSelectedPlayers(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPlayers.length === filteredPlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(filteredPlayers.map(p => p.playerId));
    }
  };

  const handleConfirm = () => {
    if (selectedPlayers.length === 0) {
      alert('Please select at least one player');
      return;
    }
    onConfirm(selectedPlayers);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">Select Players for Export</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by player name or school..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedPlayers.length} of {filteredPlayers.length} selected
            </span>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {selectedPlayers.length === filteredPlayers.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {/* Player List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading players...</div>
          ) : filteredPlayers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No players found</div>
          ) : (
            <div className="space-y-2">
              {filteredPlayers.map(player => {
                const isSelected = selectedPlayers.includes(player.playerId);
                const fullName = `${player.first_name} ${player.middle_name || ''} ${player.last_name}`.trim();
                
                return (
                  <div
                    key={player.playerId}
                    onClick={() => handleTogglePlayer(player.playerId)}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-4 ${
                      isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check size={14} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{fullName}</div>
                      <div className="text-sm text-gray-600">
                        {player.schoolName} • {exportService.formatAgeCategory(player.ageCategory)} • {player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedPlayers.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Export {selectedPlayers.length} Player{selectedPlayers.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerSelectionModal;

